// src/lib/networkSecurityScript.js
// Builds the security + monitoring + console-capture script that is injected
// into the sandboxed iframe. The returned value is a plain string — it has
// NO access to React, imports, or the parent. Keep it a pure string.
//
// Policy (see AGENTS.md):
//   inbound  (GET/HEAD/OPTIONS + passive resource loads) → allowed + logged
//   outbound (POST/PUT/PATCH/DELETE + WebSocket + form-submit) → blocked, modal
//   sendBeacon → always blocked (post-hoc whitelist in settings)
// Sensitive-keyword heuristic flags (but does not block) requests whose
// query-string keys match a sensitive name with a high-entropy value.

export function buildNetworkSecurityScript() {
  return `
    <script>
    (function () {
      'use strict';

      // ----- parent communication -----
      var send = function (data) {
        try { window.parent.postMessage(data, '*'); } catch (e) {}
      };

      var reqId = 0;
      var pending = new Map(); // id -> resolve

      // Ask the parent whether an outbound request is permitted.
      // Resolves to 'allow-once' | 'allow-always' | 'deny' (or undefined on timeout).
      function requestConfirm(api, method, url, body, headers) {
        var id = ++reqId;
        return new Promise(function (resolve) {
          pending.set(id, { resolve: resolve });
          send({ type: 'network-confirm-request', id: id, api: api,
                 method: method, url: url, body: body || '', headers: headers || {} });
          // Safety timeout so user code doesn't hang forever if the modal is ignored.
          setTimeout(function () {
            if (pending.has(id)) {
              pending.delete(id);
              resolve('deny');
            }
          }, 120000);
        });
      }

      window.addEventListener('message', function (event) {
        if (!event.data || event.data.type !== 'network-confirm-response') return;
        var h = pending.get(event.data.id);
        if (h) {
          pending.delete(event.data.id);
          h.resolve(event.data.decision);
        }
      });

      // ----- sensitive-keyword heuristic -----
      var SENSITIVE = ['token','secret','session','password','auth','credential',
        'api_key','apikey','access_token','refresh_token','bearer','jwt',
        'client_secret','private_key','authorization','csrf','cookie'];
      // 'key' is intentionally excluded as a standalone match (too generic); it
      // is still matched when it appears inside an underscore/hyphen word like
      // api_key or access_key.
      function checkSensitive(urlStr) {
        var sensitive = false, highEntropy = false;
        try {
          var u = new URL(urlStr, location.href);
          var params = u.searchParams;
          params.forEach(function (value, key) {
            var norm = key.toLowerCase();
            var words = norm.split(/[_-]/);
            var matched = SENSITIVE.indexOf(norm) !== -1 || words.some(function (w) {
              return SENSITIVE.indexOf(w) !== -1 || w === 'key';
            });
            if (matched) {
              sensitive = true;
              // High-entropy signal: long base64/hex-ish string.
              if (value.length >= 16 && /^[A-Za-z0-9+/=\\-_]+$/.test(value)) {
                highEntropy = true;
              }
            }
          });
        } catch (e) {}
        return { sensitive: sensitive, highEntropy: highEntropy };
      }

      function logNetwork(opts) {
        send(Object.assign({ type: 'network-log', timestamp: Date.now() }, opts));
      }

      // ----- CSP violation reporting -----
      document.addEventListener('securitypolicyviolation', function (e) {
        send({ type: 'csp-violation', blockedURI: e.blockedURI,
               violatedDirective: e.violatedDirective,
               sourceFile: e.sourceFile, lineNumber: e.lineNumber });
      });

      // ----- fetch hook -----
      if (typeof window.fetch === 'function') {
        var nativeFetch = window.fetch.bind(window);
        window.fetch = function (input, init) {
          try {
            var url = typeof input === 'string' ? input
                    : (input && input.url) ? input.url : String(input);
          } catch (e2) { var url = String(input); }
          var method = ((init && init.method) ? init.method
                     : (input && input.method) ? input.method : 'GET').toUpperCase();
          var body = init && init.body ? String(init && init.body).slice(0, 500) : '';
          var headersRaw = init && init.headers;
          var headers = {};
          try {
            if (headersRaw instanceof Headers) {
              headers = Object.fromEntries(headersRaw.entries());
            } else if (headersRaw && typeof headersRaw === 'object') {
              headers = headersRaw;
            }
          } catch (e3) {}
          var chk = checkSensitive(url);
          var direction = ['POST','PUT','PATCH','DELETE'].indexOf(method) !== -1 ? 'outbound' : 'inbound';
          logNetwork({ method: method, url: url, direction: direction, status: 'pending',
                       riskBadge: chk.sensitive, badge: chk.highEntropy ? 'high-entropy' : null,
                       api: 'fetch' });
          if (direction === 'outbound') {
            return requestConfirm('fetch', method, url, body, headers).then(function (decision) {
              if (decision === 'deny' || decision === undefined) {
                logNetwork({ method: method, url: url, direction: direction, status: 'blocked', api: 'fetch' });
                return Promise.reject(new TypeError('Network request blocked by FrontPlay: ' + url));
              }
              logNetwork({ method: method, url: url, direction: direction, status: 'allowed', api: 'fetch' });
              return nativeFetch(input, init);
            });
          }
          return nativeFetch(input, init);
        };
      }

      // ----- XMLHttpRequest hook -----
      if (typeof XMLHttpRequest !== 'undefined') {
        var NativeXHR = XMLHttpRequest;
        function PatchedXHR() {
          var xhr = new NativeXHR();
          var _method = 'GET';
          var _url = '';
          var _sent = false;
          var nativeOpen = xhr.open.bind(xhr);
          xhr.open = function (method, url) {
            _method = String(method || 'GET').toUpperCase();
            _url = String(url);
            return nativeOpen.apply(xhr, arguments);
          };
          var nativeSend = xhr.send.bind(xhr);
          xhr.send = function (body) {
            if (_sent) return; _sent = true;
            var chk = checkSensitive(_url);
            var direction = ['POST','PUT','PATCH','DELETE'].indexOf(_method) !== -1 ? 'outbound' : 'inbound';
            logNetwork({ method: _method, url: _url, direction: direction, status: 'pending',
                         riskBadge: chk.sensitive, badge: chk.highEntropy ? 'high-entropy' : null,
                         api: 'xhr' });
            if (direction === 'outbound') {
              requestConfirm('xhr', _method, _url, body ? String(body).slice(0, 500) : '', {}).then(function (decision) {
                if (decision === 'deny' || decision === undefined) {
                  logNetwork({ method: _method, url: _url, direction: direction, status: 'blocked', api: 'xhr' });
                  try { Object.defineProperty(xhr, 'status', { value: 0, configurable: true }); } catch (e4) {}
                  try { Object.defineProperty(xhr, 'readyState', { value: 4, configurable: true }); } catch (e5) {}
                  try { xhr.dispatchEvent(new Event('error')); } catch (e6) {}
                } else {
                  logNetwork({ method: _method, url: _url, direction: direction, status: 'allowed', api: 'xhr' });
                  nativeSend.call(xhr, body);
                }
              });
            } else {
              nativeSend.call(xhr, body);
            }
          };
          return xhr;
        }
        PatchedXHR.prototype = NativeXHR.prototype;
        window.XMLHttpRequest = PatchedXHR;
      }

      // ----- sendBeacon: always block + log -----
      if (navigator && typeof navigator.sendBeacon === 'function') {
        navigator.sendBeacon = function (url, data) {
          var chk = checkSensitive(String(url));
          logNetwork({ method: 'BEACON', url: String(url), direction: 'outbound',
                       status: 'blocked', riskBadge: chk.sensitive,
                       badge: chk.highEntropy ? 'high-entropy' : null, api: 'beacon' });
          return false;
        };
      }

      // ----- WebSocket hook (proxy to real WS once approved) -----
      if (typeof WebSocket !== 'undefined') {
        var NativeWS = WebSocket;
        function WSHandle(url, protocols) {
          var self = this;
          this.url = String(url);
          this.readyState = NativeWS.CONNECTING;
          this.binaryType = 'blob';
          this.bufferedAmount = 0;
          this.extensions = '';
          this.protocol = '';
          logNetwork({ method: 'WS', url: String(url), direction: 'outbound',
                       status: 'pending', api: 'websocket' });
          requestConfirm('websocket', 'CONNECT', String(url), '', {}).then(function (decision) {
            if (decision === 'deny' || decision === undefined) {
              logNetwork({ method: 'WS', url: self.url, direction: 'outbound',
                           status: 'blocked', api: 'websocket' });
              self.readyState = NativeWS.CLOSED;
              try { self.dispatchEvent(new Event('error')); } catch (e7) {}
              try { self.dispatchEvent(new Event('close')); } catch (e8) {}
            } else {
              logNetwork({ method: 'WS', url: self.url, direction: 'outbound',
                           status: 'allowed', api: 'websocket' });
              var real = protocols !== undefined ? new NativeWS(url, protocols) : new NativeWS(url);
              self._real = real;
              ['open','message','close','error'].forEach(function (type) {
                real.addEventListener(type, function (ev) {
                  self.readyState = real.readyState;
                  self.protocol = real.protocol;
                  self.extensions = real.extensions;
                  try { self.dispatchEvent(ev); } catch (e9) {}
                });
              });
            }
          });
        }
        WSHandle.prototype = Object.create(EventTarget.prototype);
        WSHandle.prototype.constructor = WSHandle;
        WSHandle.prototype.send = function (data) {
          if (this._real) {
            this._real.send(data);
            logNetwork({ method: 'WS-SEND', url: this.url, direction: 'outbound',
                         status: 'allowed', api: 'websocket-send' });
          }
        };
        WSHandle.prototype.close = function () {
          if (this._real) this._real.close.apply(this._real, arguments);
          else {
            this.readyState = NativeWS.CLOSED;
            try { this.dispatchEvent(new Event('close')); } catch (e10) {}
          }
        };
        Object.defineProperty(WSHandle.prototype, 'onopen', { get: function(){return this._onopen;}, set: function(fn){ this._onopen = fn; if (fn) this.addEventListener('open', fn); } });
        Object.defineProperty(WSHandle.prototype, 'onmessage', { get: function(){return this._onmessage;}, set: function(fn){ this._onmessage = fn; if (fn) this.addEventListener('message', fn); } });
        Object.defineProperty(WSHandle.prototype, 'onerror', { get: function(){return this._onerror;}, set: function(fn){ this._onerror = fn; if (fn) this.addEventListener('error', fn); } });
        Object.defineProperty(WSHandle.prototype, 'onclose', { get: function(){return this._onclose;}, set: function(fn){ this._onclose = fn; if (fn) this.addEventListener('close', fn); } });
        WSHandle.CONNECTING = NativeWS.CONNECTING;
        WSHandle.OPEN = NativeWS.OPEN;
        WSHandle.CLOSING = NativeWS.CLOSING;
        WSHandle.CLOSED = NativeWS.CLOSED;
        window.WebSocket = WSHandle;
      }

      // ----- EventSource: inbound, allowed + log -----
      if (typeof EventSource !== 'undefined') {
        var NativeES = EventSource;
        window.EventSource = function (url, config) {
          logNetwork({ method: 'GET', url: String(url), direction: 'inbound',
                       status: 'allowed', api: 'eventsource' });
          return config !== undefined ? new NativeES(url, config) : new NativeES(url);
        };
        window.EventSource.CONNECTING = NativeES.CONNECTING;
        window.EventSource.OPEN = NativeES.OPEN;
        window.EventSource.CLOSED = NativeES.CLOSED;
        window.EventSource.prototype = NativeES.prototype;
      }

      // ----- Form submit blocker -----
      var nativeFormSubmit = (typeof HTMLFormElement !== 'undefined' && HTMLFormElement.prototype)
        ? HTMLFormElement.prototype.submit : null;

      function handleFormSubmit(e) {
        var form = e.target;
        var action = form.action || location.href;
        var method = (form.method || 'get').toUpperCase();
        if (method === 'GET' || method === 'DIALOG') return; // GET forms are navigation, mostly inbound
        e.preventDefault();
        var chk = checkSensitive(action);
        logNetwork({ method: method, url: String(action), direction: 'outbound',
                     status: 'pending', riskBadge: chk.sensitive,
                     badge: chk.highEntropy ? 'high-entropy' : null, api: 'form' });
        requestConfirm('form', method, String(action), '', {}).then(function (decision) {
          if (decision === 'deny' || decision === undefined) {
            logNetwork({ method: method, url: String(action), direction: 'outbound',
                         status: 'blocked', api: 'form' });
          } else {
            logNetwork({ method: method, url: String(action), direction: 'outbound',
                         status: 'allowed', api: 'form' });
            if (nativeFormSubmit) nativeFormSubmit.call(form);
          }
        });
      }
      document.addEventListener('submit', handleFormSubmit, true);
      if (nativeFormSubmit) {
        HTMLFormElement.prototype.submit = function () {
          var action = this.action || location.href;
          var method = (this.method || 'get').toUpperCase();
          logNetwork({ method: method, url: String(action), direction: 'outbound',
                       status: 'pending', api: 'form-submit' });
          var self = this;
          requestConfirm('form-submit', method, String(action), '', {}).then(function (decision) {
            if (decision === 'deny' || decision === undefined) {
              logNetwork({ method: method, url: String(action), direction: 'outbound',
                           status: 'blocked', api: 'form-submit' });
            } else {
              logNetwork({ method: method, url: String(action), direction: 'outbound',
                           status: 'allowed', api: 'form-submit' });
              nativeFormSubmit.call(self);
            }
          });
        };
      }

      // ----- MutationObserver for passive resource loads -----
      function logResource(url, kind) {
        if (!url) return;
        logNetwork({ method: 'GET', url: String(url), direction: 'inbound',
                     status: 'allowed', api: 'resource:' + kind });
      }
      function scanNode(node) {
        if (!node || node.nodeType !== 1) return;
        var tag = node.tagName.toLowerCase();
        if (tag === 'script' && node.src) logResource(node.src, 'script');
        else if (tag === 'img' && node.src) logResource(node.src, 'img');
        else if (tag === 'link' && node.href) logResource(node.href, 'link');
        else if (tag === 'iframe' && node.src) logResource(node.src, 'iframe');
        else if (tag === 'video' && node.src) logResource(node.src, 'video');
        else if (tag === 'audio' && node.src) logResource(node.src, 'audio');
        else if (tag === 'source' && node.src) logResource(node.src, 'source');
      }
      if (typeof MutationObserver !== 'undefined') {
        var obs = new MutationObserver(function (mutations) {
          mutations.forEach(function (m) {
            m.addedNodes.forEach(scanNode);
          });
        });
        var startObserve = function () {
          try {
            obs.observe(document, { childList: true, subtree: true });
            document.querySelectorAll('script[src],img[src],link[href],iframe[src],video[src],audio[src],source[src]').forEach(function (n) { scanNode(n); });
          } catch (e12) {}
        };
        if (document.readyState === 'loading') {
          document.addEventListener('DOMContentLoaded', startObserve);
        } else {
          startObserve();
        }
      }

      // ----- localStorage note -----
      // opaque-origin sandbox iframe cannot access localStorage/sessionStorage
      // anyway (SecurityError on access). No nulification needed — leaving the
      // browser's default behavior as the intent.

      // ----- console + error capture (extended) -----
      var captures = ['log', 'info', 'warn', 'error'];
      captures.forEach(function (m) {
        var orig = console[m] ? console[m].bind(console) : function () {};
        console[m] = function () {
          orig.apply(console, arguments);
          send({ type: 'console', method: m,
                 args: Array.prototype.map.call(arguments, function (a) {
                   try { return typeof a === 'object' ? JSON.stringify(a) : String(a); }
                   catch (e13) { return String(a); }
                 }) });
        };
      });
      window.onerror = function (msg, url, line, col) {
        send({ type: 'error', message: msg, line: line, column: col });
      };
      window.addEventListener('unhandledrejection', function (e) {
        var reason = e && e.reason;
        var msg = (reason && reason.message) ? reason.message : String(reason);
        send({ type: 'error', message: 'Unhandled rejection: ' + msg, line: 0, column: 0 });
      });
    })();
    </script>
  `;
}

// CSP meta tag injected as the FIRST child of <head> so CSP is in effect before
// any user script runs. The modal layer (JS hooks) is the policy gate for
// outbound requests; CSP must therefore *allow* connect-src/form-action (so an
// approved POST actually goes through the network), and instead catches
// violating source types (e.g. ftp/file scripts, non-whitelisted media types)
// via the `securitypolicyviolation` event for logging.
export function buildCspMetaTag() {
  return [
    `<meta http-equiv="Content-Security-Policy" content="`,
    [
      "default-src 'none'",
      "script-src 'unsafe-inline' https: http:",
      "style-src 'unsafe-inline' https: http:",
      "img-src https: http: data: blob:",
      // Inbound resource loads are allowed (consistent with the inbound-OK policy)
      "media-src https: http: data: blob:",
      "frame-src https: http:",
      "font-src https: http: data:",
      // Allow connect targets so modal-approved requests go through; blocking
      // is done at the JS-hook layer (which can read method+body+headers, and
      // present the per-request confirmation).
      "connect-src https: http: ws: wss:",
      // Allow modal-approved form submissions to go through the network.
      "form-action https: http:",
      "base-uri 'none'"
    ].join("; "),
    `;">`
  ].join("");
}

// Injected into the live preview only. Lets the parent app search the rendered
// output: the parent posts { type:'preview-find', query, dir } and this script
// highlights matches and scrolls to the current one, reporting counts back via
// { type:'preview-find-result' }. Pure DOM manipulation — no network, no hooks.
export function buildPreviewFindScript() {
  return `<style>
.fp-find-hit { background: #ffe066; color: #1a1a1a; border-radius: 2px; }
.fp-find-current { background: #ff8c1a; color: #1a1a1a; }
</style>
<script>
(function () {
  var hits = [];
  var current = 0;
  var lastQ = '';
  function clearHits() {
    var arr = document.querySelectorAll('span.fp-find-hit');
    for (var i = 0; i < arr.length; i++) {
      var s = arr[i];
      var p = s.parentNode;
      if (p) { p.replaceChild(document.createTextNode(s.textContent), s); p.normalize(); }
    }
  }
  function runFind(q) {
    clearHits();
    var walker = document.createTreeWalker(
      document.body,
      NodeFilter.SHOW_TEXT,
      {
        acceptNode: function (node) {
          if (!node.nodeValue || !node.nodeValue.trim()) return NodeFilter.FILTER_REJECT;
          var p = node.parentNode;
          if (p && (p.tagName === 'SCRIPT' || p.tagName === 'STYLE' || p.tagName === 'NOSCRIPT')) {
            return NodeFilter.FILTER_REJECT;
          }
          return NodeFilter.FILTER_ACCEPT;
        }
      }
    );
    var textNodes = [];
    var n;
    while ((n = walker.nextNode())) textNodes.push(n);
    var out = [];
    var low = q.toLowerCase();
    textNodes.forEach(function (tn) {
      var text = tn.nodeValue;
      var tlow = text.toLowerCase();
      var ranges = [];
      var idx = 0;
      var m;
      while ((m = tlow.indexOf(low, idx)) !== -1) {
        ranges.push([m, m + q.length]);
        idx = m + q.length;
      }
      if (!ranges.length) return;
      var frag = document.createDocumentFragment();
      var last = 0;
      ranges.forEach(function (r) {
        if (r[0] > last) frag.appendChild(document.createTextNode(text.slice(last, r[0])));
        var span = document.createElement('span');
        span.className = 'fp-find-hit';
        span.textContent = text.slice(r[0], r[1]);
        frag.appendChild(span);
        out.push(span);
        last = r[1];
      });
      if (last < text.length) frag.appendChild(document.createTextNode(text.slice(last)));
      tn.parentNode.replaceChild(frag, tn);
    });
    return out;
  }
  function post(count, index) {
    window.parent.postMessage({ type: 'preview-find-result', count: count, index: index }, '*');
  }
  window.addEventListener('message', function (e) {
    if (!e.data || e.data.type !== 'preview-find') return;
    var q = e.data.query || '';
    if (q === '') { clearHits(); hits = []; current = 0; post(0, 0); return; }
    var dir = (typeof e.data.dir === 'number') ? e.data.dir : 0;
    if (q !== lastQ || hits.length === 0) {
      hits = runFind(q);
      lastQ = q;
      current = 0;
    } else {
      var len = hits.length;
      current = ((current + dir) % len + len) % len;
    }
    hits.forEach(function (h, i) { h.classList.toggle('fp-find-current', i === current); });
    if (hits.length) {
      try { hits[current].scrollIntoView({ block: 'center' }); } catch (_) {}
    }
    post(hits.length, hits.length ? current + 1 : 0);
  });
})();
</script>`;
}

// Scroll sync: the parent cannot read scrollY of the sandboxed (opaque-origin)
// iframe cross-origin, so we track the scroll position INSIDE the iframe and
// report it to the parent. The parent asks us to restore it via postMessage,
// which we apply on our own window (allowed, since it's our own document).
export function buildScrollSyncScript() {
  return `<script>
(function () {
  var last = { x: 0, y: 0 };
  var scheduled = false;
  function report() {
    scheduled = false;
    var x = window.pageXOffset || (document.documentElement && document.documentElement.scrollLeft) || 0;
    var y = window.pageYOffset || (document.documentElement && document.documentElement.scrollTop) || 0;
    if (x !== last.x || y !== last.y) {
      last.x = x; last.y = y;
      try { window.parent.postMessage({ type: 'fp-scroll', x: x, y: y }, '*'); } catch (e) {}
    }
  }
  function onScroll() {
    if (scheduled) return;
    scheduled = true;
    (window.requestAnimationFrame || function (f) { setTimeout(f, 16); })(report);
  }
  window.addEventListener('scroll', onScroll, true);
  window.addEventListener('load', function () { report(); });
  setTimeout(report, 0);
  window.addEventListener('message', function (e) {
    if (!e.data || e.data.type !== 'fp-set-scroll') return;
    try { window.scrollTo(e.data.x || 0, e.data.y || 0); } catch (err) {}
    // Re-report after the scroll settles (async layout / late content).
    setTimeout(report, 0);
    (window.requestAnimationFrame || function (f) { setTimeout(f, 16); })(function () { setTimeout(report, 60); });
  });
})();
</script>`;
}

// Reverse mapping: when the user selects text in the rendered preview, report the
// selected string to the parent so it can highlight the corresponding source line.
// Runs INSIDE the sandboxed iframe (opaque origin) — it can read window.getSelection()
// and post the text out via postMessage; the parent cannot read the iframe DOM directly.
export function buildSelectionLinkScript() {
  return `<script>
(function () {
  var linkOn = false;
  var t = null;
  function getSel() {
    try { return (window.getSelection() || '').toString() || ''; } catch (e) { return ''; }
  }
  function nearestLine(node) {
    var el = node && node.nodeType === 3 ? node.parentElement : node;
    while (el) {
      if (el.getAttribute && el.getAttribute('data-fp-line')) {
        return parseInt(el.getAttribute('data-fp-line'), 10);
      }
      el = el.parentElement;
    }
    return null;
  }
  function send() {
    if (!linkOn) return;
    var s = getSel().trim();
    if (s === t) return;
    t = s;
    var line = null;
    try {
      var sel = window.getSelection();
      if (sel && sel.anchorNode) line = nearestLine(sel.anchorNode);
    } catch (e) {}
    try { window.parent.postMessage({ type: 'fp-preview-selection', text: s, line: line }, '*'); } catch (e) {}
  }
  window.addEventListener('message', function (e) {
    if (!e.data || e.data.type !== 'fp-set-link') return;
    linkOn = !!e.data.on;
    if (!linkOn) { t = ''; try { window.parent.postMessage({ type: 'fp-preview-selection', text: '' }, '*'); } catch (e) {} }
  });
  document.addEventListener('selectionchange', function () { if (t === null) t = ''; clearTimeout(window.__fpLinkT); window.__fpLinkT = setTimeout(send, 120); });
  document.addEventListener('mouseup', function () { setTimeout(send, 0); });
})();
</script>`;
}